import React, {useState, useEffect} from 'react';
import 'date-fns';
import DateFnsUtils from '@date-io/date-fns';
import {
    Box,
    Button,
    Container,
    Chip,
    CircularProgress,
    ExpansionPanel,
    ExpansionPanelSummary,
    ExpansionPanelDetails,
    Grid,
    IconButton,
    makeStyles,
    Modal,
    Paper,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tabs,
    TextField,
    Typography,
} from '@material-ui/core';
import {
    MuiPickersUtilsProvider,
    KeyboardDatePicker,
} from '@material-ui/pickers';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import LaunchIcon from '@material-ui/icons/Launch';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import WarningIcon from '@material-ui/icons/Warning';
import DateRangeIcon from '@material-ui/icons/DateRange';
import DescriptionIcon from '@material-ui/icons/Description';
import {Alert, Autocomplete} from '@material-ui/lab';
import useStyles from '../styles/styles';
import {useQuery} from '@apollo/react-hooks';
import {gql} from 'apollo-boost';
import theme from '../styles/theme';
import useFormInputs from "../hooks/useFormInputs";
import ParseResponse from '../utils/ParseResponse';
import _ from 'lodash';
import setDate from '../utils/setDate';

const useBoxStyles = makeStyles({
    root: {
        height: '400px',
        overflow: 'scroll'
    },
});

const PixelLog = () => {
    const classes = useStyles();
    const boxClasses = useBoxStyles();
    const [open, setOpen] = useState(false);
    const [variables, setVariables] = useState({});
    const [modalData, setModalData] = useState({});

    const onSubmit = () => {
        let finalInputs = _.omitBy(inputs, _.isNil);

        if (finalInputs.affiliateName || finalInputs.trafficSourceName) {
            let trafficSourceIds = [];
            let affiliateIds = [];

            if (finalInputs.affiliateName) {
                affiliateIds.push(findOptionId(data.allAffiliate.nodes, finalInputs.affiliateName))
            }

            if (finalInputs.trafficSourceName) {
                trafficSourceIds.push(findOptionId(data.allTrafficSource.nodes, finalInputs.trafficSourceName));
            }

            finalInputs = {
                ...finalInputs,
                trafficSourceFilter: {
                    id: trafficSourceIds.length > 0 ? trafficSourceIds : null,
                    includeDeleted: false,
                    affiliate: {
                        id: affiliateIds.length > 0 ? affiliateIds : null
                    }
                }
            }
        }

        setVariables(finalInputs);
    };

    const [selectedTab, setSelectedTab] = useState(0);
    const {clearInput, clearAllInputs, inputs, setInputs, handleFilterChange, handleInputChange, handleSubmit} = useFormInputs(onSubmit);
    const {loading, error, data, refetch, fetchMore} = useQuery(gql`
        query AllPixelLogs(
            $limit: Int
            $skip: Long
            $startDate: DateTime
            $endDate: DateTime
            $id: UUID
            $trafficSourceFilter: TrafficSourceInputFilterInput
        ) {
            allPixelLogs(
                filter: {
                    createdAt: { gte: $startDate, lte: $endDate }
                    id: $id
                    trafficSource: $trafficSourceFilter
                }
                limit: $limit
                skip: $skip
            ) {
                nodes {
                    id
                    dateCreated
                    pixelId
                    revenueId
                    affiliateId
                    trafficSourceId
                    revenueType
                    statusCode
                    request
                    response
                    result
                }
                totalCount
            }
            allAffiliate {
                nodes {
                    id
                    name
                }
            }
            allTrafficSource {
                nodes {
                    id
                    name
                }
            }
        }
    `, {
        variables: {
            ...variables,
            limit: 30,
        },
        notifyOnNetworkStatusChange: true,
        context: {
            clientName: "kgraphql"
        }
    });

    useEffect(() => {
        if (!Object.keys(inputs).length) {
            setVariables({});
        }
    }, [inputs]);

    const handleOpen = (request, response) => {
        setModalData({
            request,
            response
        });
        setOpen(true);
    };

    const handleClose = () => {
        setModalData({});
        setOpen(false);
    };

    const handleTabClick = (e, val) => {
        setSelectedTab(val);
    };

    const findOptionName = (options, id) => {
        const option = options.find(item => item.id === id)
        return option ? option.name : null
    }

    const findOptionId = (options, name) => {
        const option = options.find(item => item.name === name)
        return option ? option.id : null
    }

    const handleChange = (name, value) => {
        setInputs(inputs => ({
            ...inputs,
            [name]: value ? value.name : ''
        }));
    };

    const PixelLogTable = () => {
        return (
            <Table className={classes.table} size="small">
                <TableHead>
                    <TableRow>
                        <TableCell variant="head">View Raw Data</TableCell>
                        <TableCell variant="head">Status</TableCell>
                        <TableCell variant="head">Id</TableCell>
                        <TableCell variant="head">Date Created</TableCell>
                        <TableCell variant="head">Pixel Id</TableCell>
                        <TableCell variant="head">Revenue Id</TableCell>
                        <TableCell variant="head">Affiliate</TableCell>
                        <TableCell variant="head">TrafficSource</TableCell>
                        <TableCell variant="head">Revenue Type</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {data.allPixelLogs.nodes.map((log) => {
                        return (
                            <TableRow key={log.id}>
                                <TableCell variant="body">
                                    <IconButton onClick={() => handleOpen(log.request, log.response)}>
                                        <LaunchIcon/>
                                    </IconButton>
                                </TableCell>
                                <TableCell variant="body">{log.statusCode === 200 ?
                                    <CheckBoxIcon style={{color: theme.palette.success.main}}/> :
                                    <WarningIcon style={{color: theme.palette.error.main}}/>}
                                </TableCell>
                                <TableCell variant="body">{log.id}</TableCell>
                                <TableCell variant="body">{setDate(log.dateCreated)}</TableCell>
                                <TableCell variant="body">{log.pixelId}</TableCell>
                                <TableCell variant="body">{log.revenueId}</TableCell>
                                <TableCell variant="body">{findOptionName(data.allAffiliate.nodes, log.affiliateId)}</TableCell>
                                <TableCell variant="body">{findOptionName(data.allTrafficSource.nodes, log.trafficSourceId)}</TableCell>
                                <TableCell variant="body">{log.revenueType}</TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        );
    };

    return (
        <>
            <Typography variant="h2">
                Pixel Log
            </Typography>
            <Container maxWidth={false} className={classes.chips}>
                {inputs.startDate || inputs.endDate ? (
                    <Chip
                        icon={<DateRangeIcon/>}
                        label="Date Range"
                        clickable
                        onClick={() => {
                            clearInput('startDate')
                            clearInput('endDate')
                        }}
                        onDelete={() => {
                            clearInput('startDate')
                            clearInput('endDate')
                        }}
                    />
                ) : null}
            </Container>
            <form id="log-form" onSubmit={handleSubmit}>
                <ExpansionPanel expanded>
                    <ExpansionPanelSummary
                        expandIcon={<ExpandMoreIcon/>}
                        aria-controls="panel1a-content"
                        id="panel1a-header"
                    >
                        <Typography variant="h5">Filter Logs</Typography>
                    </ExpansionPanelSummary>
                    <ExpansionPanelDetails className={classes.fliter}>
                        <Grid container spacing={2} justify="center" alignItems="center">
                            <Grid item xs={12}>
                                <Box className={classes.fliterBox} style={{marginTop: 0, paddingBottom: '20px'}}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={2}>
                                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                <KeyboardDatePicker
                                                    disableToolbar
                                                    variant="inline"
                                                    margin="normal"
                                                    id="date-picker-start"
                                                    label="Start Date"
                                                    value={inputs.startDate || null}
                                                    onChange={date => handleFilterChange(date, 'startDate')}
                                                    KeyboardButtonProps={{
                                                        'aria-label': 'change date',
                                                    }}
                                                    className={classes.formControl}
                                                />
                                            </MuiPickersUtilsProvider>
                                        </Grid>
                                        <Grid item xs={2}>
                                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                <KeyboardDatePicker
                                                    disableToolbar
                                                    variant="inline"
                                                    margin="normal"
                                                    id="date-picker-end"
                                                    label="End Date"
                                                    value={inputs.endDate || null}
                                                    onChange={date => handleFilterChange(date, 'endDate')}
                                                    KeyboardButtonProps={{
                                                        'aria-label': 'change date',
                                                    }}
                                                    className={classes.formControl}
                                                />
                                            </MuiPickersUtilsProvider>
                                        </Grid>
                                        <Grid item xs={4}>
                                            <Grid item xs={12}>
                                                <TextField
                                                    id="id"
                                                    name="id"
                                                    label="Search by Id"
                                                    value={inputs.id || ''}
                                                    onChange={handleInputChange}
                                                    fullWidth
                                                />
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Box>
                            </Grid>
                            <Grid item xs={12}>
                                <Grid container spacing={2}>
                                    {data && data.allAffiliate && data.allAffiliate.nodes.length ? (
                                        <Grid item xs={4}>
                                            <Autocomplete
                                                className={classes.autoComplete}
                                                name="affiliateName"
                                                value={inputs.affiliateName || null}
                                                onChange={(e, val) => handleChange('affiliateName', val)}
                                                options={data.allAffiliate.nodes}
                                                getOptionLabel={option => {
                                                    if (_.has(option, 'name')) {
                                                        return option.name;
                                                    }
                                                    return option;
                                                }}
                                                renderInput={params => <TextField {...params} label="Affiliates"
                                                                                  variant="outlined" fullWidth/>}
                                            />
                                        </Grid>
                                    ) : null}
                                    {data && data.allTrafficSource && data.allTrafficSource.nodes.length ? (
                                        <Grid item xs={4}>
                                            <Autocomplete
                                                className={classes.autoComplete}
                                                name="trafficSourceName"
                                                value={inputs.trafficSourceName || null}
                                                onChange={(e, val) => handleChange('trafficSourceName', val)}
                                                options={data.allTrafficSource.nodes}
                                                getOptionLabel={option => {
                                                    if (_.has(option, 'name')) {
                                                        return option.name;
                                                    }
                                                    return option;
                                                }}
                                                renderInput={params => <TextField {...params} label="Traffic Sources"
                                                                                  variant="outlined" fullWidth/>}
                                            />
                                        </Grid>
                                    ) : null}
                                </Grid>
                            </Grid>
                            <Grid item xs={12} sm={6} md={4} lg={3}>
                                <Button type="submit" variant="contained" color="secondary" fullWidth>
                                    Filter Results
                                </Button>
                            </Grid>
                            <Grid item xs={12} sm={6} md={4} lg={3}>
                                <Button onClick={() => {
                                    clearAllInputs();
                                    refetch({
                                        id: null,
                                        olpSessionId: null
                                    });
                                }} variant="outlined" color="secondary" fullWidth>
                                    Clear Filters
                                </Button>
                            </Grid>
                        </Grid>
                    </ExpansionPanelDetails>
                </ExpansionPanel>
            </form>
            <Paper className={classes.paper}>
                {!loading && error ? (
                    <>
                        <Alert severity="error">There was an error!</Alert>
                        {/* <pre>{JSON.stringify(error, null, 2)}</pre> */}
                    </>
                ) : null}
                {loading ? (
                    <div className={classes.loadingBar}>
                        <CircularProgress
                            size={50}
                            thickness={4}
                        />
                    </div>
                ) : null}
                {!loading && !error ? (
                    <>
                        {data && data.allPixelLogs.nodes.length ? (
                            <>
                                <PixelLogTable/>
                                {data.allPixelLogs.nodes.length !== data.allPixelLogs.totalCount ? (
                                    <Grid container spacing={2} justify="center" alignItems="center"
                                          style={{marginTop: "1rem"}}>
                                        <Grid item xs={12} sm={6} md={4} lg={3}>
                                            <Button onClick={() => {
                                                fetchMore({
                                                    variables: {
                                                        skip: data.allPixelLogs.nodes.length + 1
                                                    },
                                                    updateQuery: (prev, {fetchMoreResult}) => {
                                                        if (!fetchMoreResult) return prev;
                                                        return Object.assign({}, prev, {
                                                            allPixelLogs: {
                                                                ...prev.allPixelLogs,
                                                                nodes: [
                                                                    ...prev.allPixelLogs.nodes,
                                                                    ...fetchMoreResult.allPixelLogs.nodes
                                                                ]
                                                            }
                                                        });
                                                    }
                                                });
                                            }} variant="outlined" color="primary">
                                                Load More Logs
                                            </Button>
                                        </Grid>
                                    </Grid>
                                ) : null}
                            </>
                        ) : (
                            <Alert severity="error">No Logs at this time!</Alert>
                        )}
                    </>
                ) : null}
            </Paper>
            <Modal
                open={open}
                onClose={handleClose}
            >
                <Paper className={classes.modal}>
                    <Tabs
                        aria-label="raw data tabs"
                        value={selectedTab}
                        onChange={handleTabClick}
                        indicatorColor="secondary"
                        centered
                    >
                        <Tab
                            label="Request Raw Tab"
                            id="raw-data-0"
                        />
                        <Tab
                            label="Response Raw Tab"
                            id="request-data-0"
                        />
                    </Tabs>
                    <Box
                        component="div"
                        role="tabpanel"
                        hidden={selectedTab !== 0}
                        className={boxClasses.root}
                    >
                        {modalData.request ? (
                            <ParseResponse data={modalData.request}/>
                        ) : null}
                    </Box>
                    <Box
                        component="div"
                        role="tabpanel"
                        hidden={selectedTab !== 1}
                        className={boxClasses.root}
                    >
                        {modalData.response ? (
                            <ParseResponse data={modalData.response}/>
                        ) : null}
                    </Box>
                </Paper>
            </Modal>
        </>
    )
}

export default PixelLog;
